import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { SSRConfig, useTranslation } from 'next-i18next';
import { GetServerSideProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useForm } from 'react-hook-form';
import dynamic from 'next/dynamic';
import get from 'lodash/get';
import ContentWrapper from '../../components/common/ContentWrapper';
import Button from '../../components/common/Button';
import useAppDispatch from '../../core/redux/useAppDispatch';
import cancellationSlice from '../../core/cancellation/slice';
import type ApplicationStatuses from '../../../types/ApplicationStatuses';
import { requestWithCache } from '../../core/request';
import LoadingBox from '../../components/common/LoadingBox';
import type FooterType from '../../../types/Footer';
import translator from '../../core/helpers/translator';
import Yup, { useYupValidationResolver } from '../../core/helpers/yup';
import FormInput from '../../components/common/FormInput/FormInput';
import { generateLinks } from '../../core/helpers/link';
import PageLayout from '../../components/common/PageLayout';
import { useSelector } from '../../core/redux/store';
import useGetBooking from '../../core/booking/useGetBooking';
import useGetOtp from '../../core/cancellation/useGetOtp';
import { Locale } from '../../core/constants';

const TLLMaintenanceStatus = dynamic(
  () => import('../../components/booking/common/TLLMaintenanceStatus')
);

type FormValues = {
  bookingId: string;
};

type Props = {
  tllMaintenanceStatus?: ApplicationStatuses[0];
  footer?: FooterType;
} & SSRConfig;

const BookingId: NextPage<Props> = ({ tllMaintenanceStatus, footer }) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { userInitialized, userAttributes } = useSelector((state) => state.account);
  const { t } = useTranslation();

  const schema = Yup.object({
    bookingId: Yup.booking().id().required().trim(),
  });

  const { handleSubmit, control, formState } = useForm<FormValues>({
    defaultValues: {
      bookingId: (router?.query.booking_id as string) || '',
    },
    mode: 'all',
    resolver: useYupValidationResolver(schema),
  });

  const { isLoading: isOtpLoading, getOtp } = useGetOtp();

  const events = {
    handleSubmit: async (data: FormValues) => {
      dispatch(cancellationSlice.actions.clear());
      dispatch(cancellationSlice.actions.setBookingId({ bookingId: data?.bookingId }));
      await getOtp(data?.bookingId);
      await router.push('/guest/otp');
    },
  };

  const { isLoading, isInitialLoading, isSuccess, isError } = useGetBooking({
    bookingId: router?.query?.booking_id as string,
    locale: router.locale as string,
    userInitialized,
    accountId: userAttributes?.accountId,
    routerIsReady: router.isReady,
  });

  useEffect(() => {
    (async () => {
      if (isSuccess) {
        await router.push(`/guest/bookings/${router?.query?.booking_id}`);
      } else if (isError) {
        await getOtp(router?.query?.booking_id as string);
        await router.push('/guest/otp');
      } else if (
        !isInitialLoading &&
        router.isReady &&
        router.query?.booking_id &&
        userInitialized
      ) {
        await getOtp(router?.query?.booking_id as string);
        await router.push('/guest/otp');
      }
    })();
  }, [isSuccess, isInitialLoading, isError, router, dispatch, getOtp, userInitialized]);

  useEffect(() => {
    if (userInitialized) {
      // Backend Booking Confirmation email will pass in booking_id
      if (router?.query?.booking_id && router?.isReady) {
        dispatch(
          cancellationSlice.actions.setBookingId({
            bookingId: router?.query?.booking_id as string,
          })
        );
      }
    }
  }, [dispatch, router, router?.isReady, router?.query.booking_id, userInitialized]);

  const renderContent = () => {
    return (isLoading && isInitialLoading && router?.query?.booking_id) || isSuccess || isError ? (
      <LoadingBox />
    ) : (
      <div className="flex flex-col gap-5 rounded-md bg-white p-3 shadow-md sm:mx-auto sm:max-w-md sm:p-12">
        <FormInput
          control={control}
          name="bookingId"
          background="gray"
          size="large"
          autoComplete="off"
          placeholder={t('page:booking_id.id')}
          disabled={isOtpLoading}
          onKeyUp={(key) => {
            if (key === 'Enter') {
              handleSubmit(events.handleSubmit);
            }
          }}
        />
        <Button
          color="red"
          textColor="white"
          onClick={handleSubmit(events.handleSubmit)}
          className="w-full"
          disabled={Object.keys(formState.errors).length > 0}
          loading={isOtpLoading}
        >
          <p>{t('page:booking_id.send_pin')}</p>
        </Button>
      </div>
    );
  };

  return (
    <PageLayout
      footer={{
        content: footer,
        mini: true,
      }}
    >
      <main className="flex-1 bg-gray-50">
        <ContentWrapper>
          <h2 className="mb-6 pt-10 text-center text-title3 font-bold sm:text-title2">
            {t('page:booking_id.title')}
          </h2>

          {tllMaintenanceStatus ? (
            <TLLMaintenanceStatus
              start_date={tllMaintenanceStatus.start_date}
              end_date={tllMaintenanceStatus.end_date}
              title=" "
              unavailableService={t('page:booking_id.title')}
            />
          ) : (
            renderContent()
          )}
        </ContentWrapper>
      </main>
    </PageLayout>
  );
};

export const getServerSideProps: GetServerSideProps<SSRConfig> = async ({ locale }) => {
  const footer = await requestWithCache<FooterType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/footer?locale=${translator.locale.get(locale)}`,
    method: 'GET',
  });

  const url = new URL(`${process.env.NEXT_PUBLIC_SITE_URL}/guest/booking-id`);

  const ssrConfig = await serverSideTranslations(translator.locale.get(locale), [
    'common',
    'component',
    'page',
    'error',
  ]);

  const namespaces =
    // eslint-disable-next-line no-underscore-dangle
    locale && ssrConfig._nextI18Next.initialI18nStore[translator.locale.get(locale)];

  const translationTargets = {
    title: get(namespaces.page, 'login.title', null),
  };

  const translatedMetadata = await translator.batch.translate(translationTargets, locale);

  const metadata = {
    title: translatedMetadata.title,
    'og:title': translatedMetadata.title,
    'og:url': `${url.origin}${locale !== Locale.JAPANESE ? `/${locale}` : ''}${url.pathname}`,
  };

  return {
    props: {
      ...ssrConfig,
      locale,
      footer,
      metadata,
      url: generateLinks(url),
    },
  };
};

export default BookingId;
